import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "../components/Admin.css";
import "../components/UserInfo.css";
import "../generics/PageCharts.css";
import { FormattedMessage } from "react-intl";
import messages from "../lang/preferences";
import Axios from 'axios';
import url from '../constants/urlsPaths';
import ppeFullLogo from '../ressources/logo-with-text-green.svg';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { computeAttitudeDetails } from '../utils/attitudes';
import allNorms from '../norms.json';

// Calculate percentile for an attitude score based on normative data
const calculateAttitudePercentile = (attitudeName, score, attitudeDetails, traits) => {
  if (!attitudeDetails || !attitudeDetails[attitudeName] || !traits) return null;
  
  const attDetail = attitudeDetails[attitudeName];
  if (!attDetail.rows || attDetail.rows.length === 0) return null;
  
  // Use maleMiddleManagement as reference population (most common)
  const norms = allNorms.maleMiddleManagement;
  
  // Calculate weighted average percentile across all indicators in this attitude
  let totalWeight = 0;
  let weightedPercentileSum = 0;
  
  attDetail.rows.forEach(row => {
    const traitKey = row.backendKey;
    const traitValue = traits[traitKey];
    
    if (traitValue != null && norms[traitKey]) {
      const [lower, mid, upper] = norms[traitKey];
      let percentile;
      
      // Calculate percentile based on position in norm distribution
      if (traitValue <= lower) {
        percentile = 16; // Below 1st standard deviation
      } else if (traitValue <= mid) {
        // Linear interpolation between lower (16th) and mid (50th)
        percentile = 16 + ((traitValue - lower) / (mid - lower)) * 34;
      } else if (traitValue <= upper) {
        // Linear interpolation between mid (50th) and upper (84th)
        percentile = 50 + ((traitValue - mid) / (upper - mid)) * 34;
      } else {
        // Above 84th percentile
        percentile = 84 + Math.min(((traitValue - upper) / upper) * 16, 16);
      }
      
      weightedPercentileSum += percentile * row.normalizedWeight;
      totalWeight += row.normalizedWeight;
    }
  });
  
  return totalWeight > 0 ? Math.round(weightedPercentileSum / totalWeight) : null;
};

// Helper to safely render FormattedMessage
function SafeFormattedMessage({ msgKey }) {
  const msg = messages[msgKey];
  const valid = msg && typeof msg.id === "string" && msg.id.length > 0;
  if (valid) {
    return <FormattedMessage {...msg} />;
  }
  return msgKey || "";
}

const attitudeNameMap = {
  'Qualite': 'ATTITUDES_QUALITY_NAME',
  'Respect': 'ATTITUDES_RESPECT_NAME',
  'Cooperation': 'ATTITUDES_COOPERATION_NAME',
  'Engagement': 'ATTITUDES_ENGAGEMENT_NAME',
  'Ouverture': 'ATTITUDES_OPENNESS_NAME'
};

// Calculate statistics for all attitudes (for reference group analysis)
const calculateAttitudeStats = (scores) => {
  if (!scores || scores.length === 0) return null;
  
  const stats = {};
  const attitudeNames = ['Qualite', 'Respect', 'Cooperation', 'Engagement', 'Ouverture'];
  
  // Reference data calculated from high-performer candidates grouped by attitudes
  // Source: backend/scripts/analyze_high_performers.js
  // Generated from Liste_par_attitude_fondamentale.xlsx candidate lists
  // Each attitude's reference group consists of candidates specifically linked to that attitude
  const referenceData = {
    'Qualite': { mean: 3.08, median: 3.04, stdDev: 0.36 },
    'Respect': { mean: 3.09, median: 3.21, stdDev: 0.29 },
    'Cooperation': { mean: 3.45, median: 3.59, stdDev: 0.33 },
    'Engagement': { mean: 3.52, median: 3.65, stdDev: 0.33 },
    'Ouverture': { mean: 1.52, median: 1.54, stdDev: 0.23 }
  };
  
  attitudeNames.forEach(name => {
    const att = scores.find(s => s.name === name);
    if (att && referenceData[name]) {
      const score = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
      const ref = referenceData[name];
      
      // Calculate Z-score: (score - mean) / stdDev
      const zScore = (score - ref.mean) / ref.stdDev;
      
      // Calculate difference from reference mean and median
      const diffFromMean = score - ref.mean;
      const diffFromMedian = score - ref.median;
      
      stats[name] = {
        score: score,
        mean: ref.mean,
        median: ref.median,
        stdDev: ref.stdDev,
        zScore: zScore,
        diffFromMean: diffFromMean,
        diffFromMedian: diffFromMedian
      };
    }
  });
  
  return stats;
};

// Percentiles provided by user (P25 / Médiane / P75)
const providedPercentiles = {
  'Affect regulation': { p25: 4.04, p50: 4.25, p75: 4.67 },
  'Affection': { p25: 2.76, p50: 3.80, p75: 4.21 },
  'Aggressiveness': { p25: 1.94, p50: 2.29, p75: 2.76 },
  'Anxiety': { p25: 3.32, p50: 3.62, p75: 3.84 },
  'Cognitive style': { p25: 3.28, p50: 3.63, p75: 4.25 },
  'Conscientiousness': { p25: 4.14, p50: 4.31, p75: 4.60 },
  'Dominance': { p25: 2.47, p50: 2.90, p75: 3.53 },
  'Emotion repression': { p25: 2.71, p50: 3.12, p75: 3.56 },
  'Enjoy work': { p25: 3.30, p50: 3.85, p75: 4.34 },
  'Excitement seeking': { p25: 2.26, p50: 3.20, p75: 3.68 },
  'Expressiveness': { p25: 3.29, p50: 3.48, p75: 3.87 },
  'Extraversion': { p25: 2.48, p50: 3.56, p75: 4.01 },
  'Harmony': { p25: 2.78, p50: 3.24, p75: 3.91 },
  'Impulsivity': { p25: 2.14, p50: 2.57, p75: 3.19 },
  'Integrity': { p25: 3.24, p50: 3.64, p75: 4.03 },
  'Intellectual stimulation': { p25: 3.27, p50: 3.33, p75: 3.80 },
  'Internal attribution': { p25: 2.20, p50: 2.76, p75: 3.50 },
  'Irritability': { p25: 2.60, p50: 3.00, p75: 3.65 },
  'Job security': { p25: 2.09, p50: 2.73, p75: 3.28 },
  'Leadership': { p25: 1.66, p50: 2.20, p75: 2.73 },
  'Morality': { p25: 3.22, p50: 3.62, p75: 3.91 },
  'Negativity': { p25: 2.08, p50: 3.00, p75: 3.54 },
  'Pessimism': { p25: 1.63, p50: 1.89, p75: 2.20 },
  'Power': { p25: 1.89, p50: 2.20, p75: 2.38 },
  'Problem solving': { p25: 3.47, p50: 3.94, p75: 4.25 },
  'Rumination': { p25: 1.83, p50: 2.14, p75: 2.84 },
  'Salary': { p25: 2.15, p50: 2.20, p75: 2.73 },
  'Seeking empathy': { p25: 1.51, p50: 1.83, p75: 2.13 },
  'Seeking social support': { p25: 3.27, p50: 3.76, p75: 4.09 },
  'Self assurance': { p25: 2.70, p50: 3.32, p75: 4.00 },
  'Self concept bolstering': { p25: 2.75, p50: 3.25, p75: 3.88 },
  'Self concept modification': { p25: 3.56, p50: 3.94, p75: 4.16 },
  'Self doubt': { p25: 3.08, p50: 3.63, p75: 3.88 },
  'Serenity': { p25: 1.48, p50: 1.84, p75: 2.77 },
  'Stress resilience': { p25: 4.07, p50: 4.34, p75: 4.70 },
  'Success': { p25: 1.66, p50: 1.85, p75: 1.97 },
  'Task variability': { p25: 2.70, p50: 3.27, p75: 3.80 },
  'Wishful thinking': { p25: 1.74, p50: 2.10, p75: 2.60 },
  'Qualité (score)': { p25: 3.74, p50: 3.94, p75: 4.10 },
  'Respect (score)': { p25: 3.68, p50: 3.96, p75: 4.13 },
  'Coopération (score)': { p25: 3.54, p50: 3.72, p75: 4.01 },
  'Engagement (score)': { p25: 3.80, p50: 4.00, p75: 4.16 },
  'Ouverture au changement (score)': { p25: 3.50, p50: 3.90, p75: 4.17 }
};

// Alternative calculation for attitude stats using trait mappings with weights
const calculateAttitudeStatsAlternative = (traits) => {
  if (!traits) return null;
  
  const stats = {};
  const attitudeNames = ['Qualite', 'Respect', 'Cooperation', 'Engagement', 'Ouverture'];
  
  // Trait mappings for each attitude with weights
  const attitudeTraitMapping = {
    'Qualite': [
      { key: 'conscientiousness', weight: 1, positive: true },
      { key: 'integrity', weight: 1, positive: true },
      { key: 'impulsivity', weight: 2, positive: false },
      { key: 'anxiety', weight: 2, positive: false }
    ],
    'Respect': [
      { key: 'integrity', weight: 1, positive: true },
      { key: 'morality', weight: 1, positive: true },
      { key: 'harmony', weight: 1, positive: true },
      { key: 'aggressiveness', weight: 2, positive: false },
      { key: 'irritability', weight: 2, positive: false }
    ],
    'Cooperation': [
      { key: 'harmony', weight: 1, positive: true },
      { key: 'seekingEmpathy', weight: 1, positive: true },
      { key: 'dominance', weight: 2, positive: false },
      { key: 'aggressiveness', weight: 2, positive: false }
    ],
    'Engagement': [
      { key: 'enjoyWork', weight: 2, positive: true },
      { key: 'conscientiousness', weight: 2, positive: true },
      { key: 'selfConsciousness', weight: 1, positive: false }  // Assuming Self doubt is selfConsciousness
    ],
    'Ouverture': [
      { key: 'intellectualStimulation', weight: 1, positive: true },
      { key: 'taskVariability', weight: 1, positive: true },
      { key: 'security', weight: 2, positive: false }  // Job security
    ]
  };
  
  // Reference data (same as original)
  const referenceData = {
    'Qualite': { mean: 3.08, median: 3.04, stdDev: 0.36 },
    'Respect': { mean: 3.09, median: 3.21, stdDev: 0.29 },
    'Cooperation': { mean: 3.45, median: 3.59, stdDev: 0.33 },
    'Engagement': { mean: 3.52, median: 3.65, stdDev: 0.33 },
    'Ouverture': { mean: 1.52, median: 1.54, stdDev: 0.23 }
  };
  
  attitudeNames.forEach(name => {
    const mapping = attitudeTraitMapping[name];
    if (mapping && referenceData[name]) {
      let totalWeightedScore = 0;
      let totalWeight = 0;
      
      mapping.forEach(trait => {
        const traitValue = traits[trait.key];
        if (traitValue != null) {
          const adjustedValue = trait.positive ? traitValue : (7 - traitValue);
          totalWeightedScore += adjustedValue * trait.weight;
          totalWeight += trait.weight;
        }
      });
      
      if (totalWeight > 0) {
        const score = totalWeightedScore / totalWeight;
        const ref = referenceData[name];
        
        const zScore = (score - ref.mean) / ref.stdDev;
        const diffFromMean = score - ref.mean;
        const diffFromMedian = score - ref.median;

        // Approximate percentiles (P25 / P75) assuming approximately normal distribution
        const z25 = -0.67448975; // z for 25th percentile
        const p25 = ref.mean + z25 * ref.stdDev;
        const p75 = ref.mean - z25 * ref.stdDev; // symmetric

        stats[name] = {
          score: score,
          mean: ref.mean,
          median: ref.median,
          stdDev: ref.stdDev,
          zScore: zScore,
          diffFromMean: diffFromMean,
          diffFromMedian: diffFromMedian,
          p25: p25,
          p50: ref.median,
          p75: p75
        };
      }
    }
  });
  
  return stats;
};

// Fetch attitude scores for a user from backend results
const fetchAttitudeScores = async (userId) => {
  if (!userId) return [];
  try {
    const response = await Axios.post(url.getResults, { code: userId }, {
      headers: {
        "x-access-token": (() => {
          const token = sessionStorage.getItem("token");
          if (!token || token === "null" || token === "undefined") {
            return null;
          }
          try {
            return JSON.parse(token);
          } catch (e) {
            return null;
          }
        })()
      }
    });

    // eslint-disable-next-line no-console
    console.log('DEBUG: Full response.data =', response.data);

    let attitudes = response.data?.attitudes || response.data?.results?.attitudes;
    if (!attitudes && Array.isArray(response.data?.results)) {
      for (const r of response.data.results) {
        if (r.attitudes) { attitudes = r.attitudes; break; }
      }
    }

    let attitudeDetails = response.data?.attitudeDetails || response.data?.results?.attitudeDetails || null;
    let rawTraits = null; // Store raw trait values for percentile calculation
    
    // Extract trait values from results array
    if (Array.isArray(response.data?.results)) {
      const personality = response.data.results.find(r => r.title === 'SCOPEREPORT_PERSONALITY');
      const coping = response.data.results.find(r => r.title === 'SCOPEREPORT_COPING');
      const action = response.data.results.find(r => r.title === 'SCOPEREPORT_ACTIONORIENTATION');
      const valuesWork = response.data.results.find(r => r.title === 'SCOPEREPORT_VALUEPREFERENCES');
      const valuesPersonal = response.data.results.find(r => r.title === 'SCOPEREPORT_VALUEPREFERENCES2');

      if (personality && coping && action && valuesWork && valuesPersonal) {
        const p = personality.data || [];
        const c = coping.data || [];
        const a = action.data || [];
        const vw = valuesWork.data || [];
        const vp = valuesPersonal.data || [];

        rawTraits = {
          extraversion: parseFloat(p[0]?.user) || 0,
          excitementSeeking: parseFloat(p[1]?.user) || 0,
          impulsivity: parseFloat(p[2]?.user) || 0,
          pessimism: parseFloat(p[3]?.user) || 0,
          selfAssurance: parseFloat(p[4]?.user) || 0,
          dominance: parseFloat(p[5]?.user) || 0,
          aggressiveness: parseFloat(p[6]?.user) || 0,
          conscientiousness: parseFloat(p[7]?.user) || 0,
          capacityForReasoning: parseFloat(p[8]?.user) || 0,
          affectRegulation: parseFloat(a[1]?.user) || 0,
          expressiveness: parseFloat(a[0]?.user) || 0,
          stressResilience: parseFloat(a[2]?.user) || 0,
          wishfulThinking: parseFloat(c[7]?.user) || 0,
          seekingSocialSupport: parseFloat(c[6]?.user) || 0,
          affection: parseFloat(vp[4]?.user) || 0,
          harmony: parseFloat(vp[5]?.user) || 0,
          morality: parseFloat(vp[2]?.user) || 0,
          integrity: parseFloat(vp[3]?.user) || 0,
          enjoyWork: parseFloat(vw[0]?.user) || 0,
          intellectualStimulation: parseFloat(vw[3]?.user) || 0,
          taskVariability: parseFloat(vw[5]?.user) || 0,
          security: parseFloat(vw[2]?.user) || 0,
        };
        
        // eslint-disable-next-line no-console
        console.log('DEBUG: Extracted rawTraits from results:', rawTraits);
      }
    }
    
    if (!attitudeDetails && rawTraits) {
      // If backend didn't provide attitudeDetails, compute them client-side
      try {
        attitudeDetails = computeAttitudeDetails(rawTraits);
        // eslint-disable-next-line no-console
        console.groupCollapsed && console.groupCollapsed('Attitude calculation details (computed client-side)');
        // eslint-disable-next-line no-console
        Object.keys(attitudeDetails).forEach(att => {
          // eslint-disable-next-line no-console
          console.group && console.group(att + ' — score: ' + (attitudeDetails[att].score != null ? attitudeDetails[att].score.toFixed(3) : 'n/a'));
          // eslint-disable-next-line no-console
          console.table && console.table(attitudeDetails[att].rows.map(r => ({ indicator: r.indicator, backendKey: r.backendKey, value: r.value, weight: r.weightPercent, normalizedWeight: Number(r.normalizedWeight.toFixed(2)), polarity: r.polarity, contrib: Number(r.contrib.toFixed(3)) })));
          // eslint-disable-next-line no-console
          console.groupEnd && console.groupEnd();
        });
        // eslint-disable-next-line no-console
        console.groupEnd && console.groupEnd();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to compute attitude details client-side:', e);
      }
    } else if (attitudeDetails) {
      // Pretty-print server-provided details for debugging (grouped + table)
      try {
        // eslint-disable-next-line no-console
        console.groupCollapsed && console.groupCollapsed('Attitude calculation details (server)');
        // eslint-disable-next-line no-console
        Object.keys(attitudeDetails).forEach(att => {
          const entry = attitudeDetails[att] || {};
          const rows = Array.isArray(entry.rows) ? entry.rows : [];
          // eslint-disable-next-line no-console
          console.group && console.group(att + ' — score: ' + (entry.score != null ? Number(entry.score).toFixed(3) : 'n/a'));
          // eslint-disable-next-line no-console
          console.table && console.table(rows.map(r => ({ indicator: r.indicator, backendKey: r.backendKey, value: r.value, weight: r.weightPercent, normalizedWeight: r.normalizedWeight != null ? Number(r.normalizedWeight.toFixed(2)) : r.normalizedWeight, polarity: r.polarity, contrib: r.contrib != null ? Number(r.contrib.toFixed(3)) : r.contrib })));
          // eslint-disable-next-line no-console
          console.groupEnd && console.groupEnd();
        });
        // eslint-disable-next-line no-console
        console.groupEnd && console.groupEnd();
      } catch (e) {
        // Fallback to simple log if something unexpected is present
        // eslint-disable-next-line no-console
        console.log('Attitude details (per-attitude):', attitudeDetails);
      }
    }

    if (!attitudes) return { attitudes: [], rawTraits };
    if (!Array.isArray(attitudes)) {
      attitudes = Object.entries(attitudes).filter(([name, value]) => value !== null).map(([name, value]) => {
        const score = value && typeof value === 'object' ? value.score : (typeof value === 'number' ? value : 0);
        const description = value && typeof value === 'object' ? value.description : '';
        return { name, score, description };
      });
    }
    
    // Add percentile information to each attitude
    // eslint-disable-next-line no-console
    console.log('DEBUG: rawTraits =', rawTraits);
    // eslint-disable-next-line no-console
    console.log('DEBUG: attitudes =', attitudes);
    // eslint-disable-next-line no-console
    console.log('DEBUG: attitudeDetails =', attitudeDetails);
    
    if (rawTraits && attitudes.length > 0) {
      attitudes = attitudes.map(att => {
        const percentile = calculateAttitudePercentile(att.name, att.score, attitudeDetails, rawTraits);
        // eslint-disable-next-line no-console
        console.log(`DEBUG: Percentile for ${att.name} = ${percentile}`);
        return {
          ...att,
          percentile
        };
      });
    }
    
    return { attitudes, rawTraits };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch attitude scores:', e);
    return [];
  }
};

export default function AttitudesPageTwo(props) {
  const location = useLocation();
  const user = props.user || location.state?.user || {};
  const userId = user.code;
  const [traitsState, setTraitsState] = useState(location.state?.traits || {});
  const traits = Object.keys(traitsState).length ? traitsState : (location.state?.traits || {});
  const onBack = props.onBack || (() => {});
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const embedded = props.embedded === true;
  // UI mode: when true show only the final interpretation table (hide other tables and labels)
  const showOnlyInterpretation = true;

  useEffect(() => {
    let mounted = true;
    fetchAttitudeScores(userId).then((data) => {
      if (!mounted) return;
      // fetchAttitudeScores now returns { attitudes, rawTraits }
      setScores(data.attitudes || []);
      setTraitsState(data.rawTraits || {});
      setLoading(false);
    }).catch(() => {
      if (!mounted) return;
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [userId]);

  // Page 1: Introduction and Raw Scores
  const page1Jsx = (
    <div className="PageCharts">
      <div className="header-logo-box">
          <img
            alt="ppe-logo"
            className="header-logo-img"
            src={ppeFullLogo}
          />
      </div>
      <div>
        <div className="PageCharts-chart">
          <div style={{
            height: '845pt',
            margin: '0 auto',
            paddingTop: '80px',
            paddingRight: '20px',
            paddingLeft: '20px',
            width:'80%'
          }}>
           { !embedded && <h1 style={{
              color: 'var(--ppe-green)',
              fontSize: '28px',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '10px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              <SafeFormattedMessage msgKey="ATTITUDES_REPORT_TITLE" />
            </h1>
          }
            {user && !embedded && (
              <div style={{
                textAlign: 'center',
                marginBottom: '30px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h3 style={{ color: '#495057', margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_ASSESSMENT_FOR_CANDIDATE" /> {user.code}
                </h3>
                <span style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: '14px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_REPORT_GENERATED_ON" /> {new Date().toLocaleDateString()}TREE
                </span>
              </div>
            )}

            <div style={{ marginBottom: '40px', lineHeight: '1.3', fontSize: '16px', color: '#495057' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '22px', fontWeight: 'bold', marginBottom: '16px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_FUNDAMENTAL_ATTITUDES" /><span className="inline font-16"> – {((props.localeDisplay || (user && user.language)) === 'fr') ? 'Vos résultats' : 'Your results'}</span>
              </h2>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px', fontSize: '18px', color: '#6c757d' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_LOADING_DATA" />
              </div>
            ) : !userId ? (
              <div style={{ color: 'red', textAlign: 'center', padding: '40px', fontSize: '16px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_NO_USER_SELECTED" />
              </div>
            ) : (
              <div>

                  <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: 'var(--ppe-green-10)', borderRadius: '8px', border: '1px solid #e1bee7', position: 'relative', zIndex: '10', width: '84%', marginLeft: 'auto', marginRight: 'auto'  }}>
                  {/* <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                      <SafeFormattedMessage msgKey="ATTITUDES_GAP_REFERENCE_GROUP" />
                    </h3>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                      <SafeFormattedMessage msgKey="ATTITUDES_ABSOLUTE_DIFFERENCE_HIGH_PERFORMERS" />
                    </p>*/}
                    { !showOnlyInterpretation && (() => {
                      const stats = calculateAttitudeStatsAlternative(traits);
                      if (!stats) return null;
                      
                      return (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--ppe-green)', color: 'white' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_ATTITUDE" /></th>

                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_INTERPRETATION" /></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(stats).map(([name, data], idx) => {
                                const diff = data.diffFromMean;
                                let interpretation = '';
                                let bgColor = '#fff';
                                
                                if (diff >= 0.5) {
                                  interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />;
                                  bgColor = '#c8e6c9';
                                } else if (diff <= -0.5) {
                                  interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />;
                                  bgColor = '#ffccbc';
                                } else {
                                  interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />;
                                  bgColor = '#fff9c4';
                                }
                                
                                return (
                                  <tr key={name} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                    <td style={{ padding: '12px', fontWeight: '600', borderBottom: '1px solid #e0e0e0' }}><SafeFormattedMessage msgKey={attitudeNameMap[name]} /></td>

                                    <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', color: '#6f6f6fff', backgroundColor: bgColor, fontWeight: '500' }}>
                                      {interpretation}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })() }

                    { !showOnlyInterpretation && (() => {
                      const stats = calculateAttitudeStatsAlternative(traits);
                      if (!stats) return null;
                      const fmt = (v) => (v == null ? '' : (typeof v === 'number' ? v.toFixed(2) : v));
                      return (
                        <div style={{ marginTop: '20px', overflowX: 'auto' }}>
                          <h4 style={{ margin: '8px 0', color: 'var(--ppe-green)', fontWeight: '600' }}><SafeFormattedMessage msgKey="ATTITUDES_ALTERNATIVE_CALC_DETAILED_TITLE" /></h4>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#4caf50', color: '#fff' }}>
                                <th style={{ padding: '10px', textAlign: 'left' }}><SafeFormattedMessage msgKey="ATTITUDES_ATTITUDE" /></th>
                                <th style={{ padding: '10px', textAlign: 'center' }}>Score</th>
                                <th style={{ padding: '10px', textAlign: 'center' }}>P25</th>
                                <th style={{ padding: '10px', textAlign: 'center' }}>Median</th>
                                <th style={{ padding: '10px', textAlign: 'center' }}>P75</th>
                                <th style={{ padding: '10px', textAlign: 'center' }}>Z</th>
                                <th style={{ padding: '10px', textAlign: 'center' }}><SafeFormattedMessage msgKey="ATTITUDES_INTERPRETATION" /></th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(stats).map(([name, data], idx) => {
                                let interpretation = '';
                                let bgColor = '#fff';
                                if (data.p25 != null && data.p75 != null) {
                                  if (data.score > data.p75) { interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />; bgColor = '#c8e6c9'; }
                                  else if (data.score >= data.p25 && data.score <= data.p75) { interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />; bgColor = '#fff9c4'; }
                                  else { interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />; bgColor = '#ffccbc'; }
                                } else {
                                  const diff = data.diffFromMean;
                                  if (diff >= 0.5) { interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />; bgColor = '#c8e6c9'; }
                                  else if (diff <= -0.5) { interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />; bgColor = '#ffccbc'; }
                                  else { interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />; bgColor = '#fff9c4'; }
                                }
                                return (
                                  <tr key={'detail-'+name} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                    <td style={{ padding: '10px', fontWeight: '600' }}><SafeFormattedMessage msgKey={attitudeNameMap[name]} /></td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmt(data.score)}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmt(data.p25)}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmt(data.p50)}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmt(data.p75)}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{fmt(data.zScore)}</td>
                                    <td style={{ padding: '10px', textAlign: 'center', backgroundColor: bgColor }}>{interpretation}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })() }

                    

                    {/* INTERPRETATION TABLE: compute interpretations for the five attitudes using provided percentiles */}
                    {(() => {
                      const stats = calculateAttitudeStatsAlternative(traits);
                      if (!stats) return null;

                      // mapping from internal attitude keys to providedPercentiles keys
                      const provKeyMap = {
                        'Qualite': 'Qualité (score)',
                        'Respect': 'Respect (score)',
                        'Cooperation': 'Coopération (score)',
                        'Engagement': 'Engagement (score)',
                        'Ouverture': 'Ouverture au changement (score)'
                      };

                      const decideInterpretation = (name, data) => {
                        const provKey = provKeyMap[name];
                        const provided = providedPercentiles[provKey];
                        const score = data && typeof data.score === 'number' ? data.score : parseFloat(data && data.score) || 0;
                        // prefer percentile-based decision when provided
                        if (provided) {
                          if (score > provided.p75) return 'ATTITUDES_CLEARLY_ABOVE';
                          if (score >= provided.p25 && score <= provided.p75) return 'ATTITUDES_COMPARABLE';
                          if (score < provided.p25) return 'ATTITUDES_CLEARLY_BELOW';
                        }
                        // fallback to simplified diff-from-mean thresholds (three levels)
                        if (data.diffFromMean != null) {
                          const diff = data.diffFromMean;
                          if (diff >= 0.5) return 'ATTITUDES_CLEARLY_ABOVE';
                          if (diff <= -0.5) return 'ATTITUDES_CLEARLY_BELOW';
                          return 'ATTITUDES_COMPARABLE';
                        }
                        return '';
                      };

                      const attitudeOrder = ['Qualite','Respect','Cooperation','Engagement','Ouverture'];
                      return (
                        <div style={{ marginTop: '20px', overflowX: 'auto'}}>
                          <h4 style={{ margin: '8px 0', color: 'var(--ppe-green)', fontWeight: '600', display: 'none' }}><SafeFormattedMessage msgKey="ATTITUDES_ALTERNATIVE_INTERPRETATION_TITLE" /></h4>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: 'var(--ppe-green)', color: 'white' }}>
                                <th style={{ padding: '12px', textAlign: 'left' }}><SafeFormattedMessage msgKey="ATTITUDES_ATTITUDE" /></th>
                                <th style={{ padding: '12px', textAlign: 'center' }}><SafeFormattedMessage msgKey="ATTITUDES_INTERPRETATION" /></th>
                              </tr>
                            </thead>
                            <tbody>
                              {attitudeOrder.map((name, idx) => {
                                const data = stats[name] || {};
                                const key = decideInterpretation(name, data);
                                // const interpretation = key ? <SafeFormattedMessage msgKey={key} /> : '';
                                let bgColor = '#fff';
                                // color mapping for three levels
                                if (key === 'ATTITUDES_CLEARLY_ABOVE') bgColor = 'var(--ppe-green-80)';
                                else if (key === 'ATTITUDES_COMPARABLE') bgColor = 'var(--ppe-green-40)';
                                else if (key === 'ATTITUDES_CLEARLY_BELOW') bgColor = 'var(--ppe-orange-60)';

                                return (
                                  <tr key={'int-'+name} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                    <td style={{ padding: '12px', fontWeight: '600' }}><SafeFormattedMessage msgKey={attitudeNameMap[name]} /></td>
                                    <td
                                      style={{ padding: '12px', borderLeft: '1px solid #ababab62', borderTop: '0.1px solid #8f8f8f6a', textAlign: 'center', backgroundColor: bgColor }}
                                      title={key || undefined}
                                      aria-label={key || undefined}
                                    />
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {/* Legend below the table */}
                          <div style={{ marginTop: '32px', marginBottom: '8px', fontSize: '15px', color: '#444' }}>
                           
                            {/* Tiers supérieur */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#222', maxWidth: 600 }}>
                              <span style={{ display: 'inline-block', width: 22, height: 22, background: 'var(--ppe-green-80)', borderRadius: 4, marginRight: 12, flexShrink: 0, marginTop: 5 }}></span>
                              <span style={{ fontSize: 16, lineHeight: 1.5 }}>
                                {((props.localeDisplay || (user && user.language)) === 'fr') ? (
                                  <>Cette attitude est bien ancrée dans le fonctionnement naturel. C'est une force réelle sur laquelle s'appuyer — tout en restant attentif·ve à la mobiliser avec discernement selon les situations.</>
                                ) : (
                                  <>This attitude is well embedded in one's natural way of functioning. It is a genuine strength to build on — while remaining mindful of deploying it with discernment depending on the situation.</>
                                )}
                              </span>
                            </div>
                            {/* Tiers moyen */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#222', maxWidth: 600 }}>
                              <span style={{ display: 'inline-block', width: 22, height: 22, background: 'var(--ppe-green-40)', borderRadius: 4, marginRight: 12, flexShrink: 0, marginTop: 5 }}></span>
                              <span style={{ fontSize: 16, lineHeight: 1.5 }}>
                                {((props.localeDisplay || (user && user.language)) === 'fr') ? (
                                  <>Cette attitude est présente dans le répertoire comportemental. Il existe des opportunités de la renforcer et de la rendre plus visible ou plus constante dans le quotidien professionnel.</>
                                ) : (
                                  <>This attitude is present in one's behavioural repertoire. There are opportunities to strengthen it and make it more visible or more consistent in day-to-day professional life.</>
                                )}
                              </span>
                            </div>
                            {/* Tiers inférieur */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', color: '#222', maxWidth: 600 }}>
                              <span style={{ display: 'inline-block', width: 22, height: 22, background: 'var(--ppe-orange-60)', borderRadius: 4, marginRight: 12, flexShrink: 0, marginTop: 5 }}></span>
                              <span style={{ fontSize: 16, lineHeight: 1.5 }}>
                                {((props.localeDisplay || (user && user.language)) === 'fr') ? (
                                  <>Cette attitude représente un axe de développement prioritaire. Comprendre dans quels contextes elle est la plus difficile à mobiliser permet d'en faire un levier de progression concret.</>
                                ) : (
                                  <>This attitude represents a priority area for development. Understanding in which contexts it is most difficult to demonstrate makes it possible to turn it into a concrete lever for progress.</>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
              </div>
              
            )}
          </div>
                   <div className="pdf-footer">
            <SafeFormattedMessage msgKey="PAGE" /> {typeof props.pageNumber === 'number' ? props.pageNumber : 14}
            <div className="pdf-footer-separator">
              <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
            </div>{" "}
            {user.code}
            <div className="pdf-footer-separator">
              <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {new Date(user.date).toLocaleDateString(
                  props.localeDisplay || undefined,
                  { day: "2-digit", month: "long", year: "numeric" }
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Page 2: Percentile Ranking
  const page2Jsx = scores.length > 0 ? (
    <div className="PageCharts">
      <div>
        <div className="PageCharts-chart">
          <div style={{
            height: '845pt',
            margin: '0 auto',
            padding: '40px 20px',
            width:'80%'
          }}>
            {!loading && userId && (
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>
                  { !embedded  && <span><SafeFormattedMessage msgKey="ATTITUDES_PERCENTILE_RANKING" /></span> }
                </h3>
                <p style={{ textAlign: 'center', fontSize: '14px', color: '#666', marginBottom: '20px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_RELATIVE_POSITION_REFERENCE_POPULATION" />
                </p>
                {scores.some(s => s.percentile != null) ? (
                  <div style={{ height: '280px', width: '100%', marginLeft: '-100px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {(() => {
                        const chartData = scores
                          .filter(att => att.percentile != null)
                          .map(att => {
                            const percentile = att.percentile;
                            const rawScore = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
                            let color = '#4caf50'; // Green
                            if (percentile < 30) color = '#ff9800'; // Orange
                            if (percentile < 16) color = '#f44336'; // Red
                            if (percentile >= 84) color = '#2e7d32'; // Dark green
                            
                            return { 
                              name: att.name, 
                              value: percentile, 
                              percentile: percentile,
                              rawScore: rawScore.toFixed(2),
                              color: color
                            };
                          });
                        return (
                          <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis 
                              type="number" 
                              domain={[0, 100]} 
                              ticks={[0, 16, 30, 50, 70, 84, 100]}
                              label={{ value: 'Percentile', position: 'insideBottom', offset: -5 }}
                            />
                            <YAxis type="category" dataKey="name" width={150} />
                            <Tooltip 
                              formatter={(value, name, props) => [
                                `${props.payload.percentile}e percentile (Score: ${props.payload.rawScore})`, 
                                'Position'
                              ]} 
                              labelFormatter={(label) => `Attitude: ${label}`}
                              contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }}
                            />
                            <Bar dataKey="value">
                              {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        );
                      })()}
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}><SafeFormattedMessage msgKey="ATTITUDES_PERCENTILE_DATA_NOT_AVAILABLE" /></p>
                )}
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', fontSize: '13px', color: '#555' }}>
                  <strong><SafeFormattedMessage msgKey="ATTITUDES_LEGEND" /></strong>
                  <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '8px', flexWrap: 'wrap' }}>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#2e7d32', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_TOP_16_EXCELLENT" /></span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4caf50', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_ABOVE_AVERAGE" /></span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ff9800', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SLIGHTLY_BELOW" /></span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_DEVELOPMENT_ZONE" /></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;
  const additionalAnalysesJsx = scores.length > 0 ? (
    <>
      {/* Page 3: Expanded X-Axis */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_1" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_RAW_SCORES_EXPANDED_VIEW" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_ZOOM_REAL_RANGE" />
                </p>
                <div style={{ height: '260px', width: '100%', marginLeft: '-100px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    {(() => {
                      const chartData = scores.map(att => {
                        const rawScore = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
                        return { name: att.name, value: rawScore, rawScore: rawScore.toFixed(2) };
                      });
                      return (
                        <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                          <XAxis type="number" domain={[1.0, 4.0]} ticks={[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0]} label={{ value: 'Score', position: 'insideBottom', offset: -5 }} />
                          <YAxis type="category" dataKey="name" width={150} />
                          <Tooltip formatter={(value, name, props) => [props.payload.rawScore, 'Score']} labelFormatter={(label) => `Attitude: ${label}`} />
                          <Bar dataKey="value">
                            {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={'#2196f3'} />)}
                          </Bar>
                        </BarChart>
                      );
                    })()}
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
{ <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>}
      {/* Page 4: Standard Deviation Bands */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_2" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_POSITION_RELATIVE_HIGH_PERFORMERS_MEAN" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_REFERENCE_HIGH_PERFORMERS_MANAGEMENT" />
                </p>
                {(() => {
                  const stats = calculateAttitudeStats(scores);
                  if (!stats) return null;
                  
                  const chartData = Object.entries(stats).map(([name, data]) => {
                    let category = 'Moyenne';
                    let color = '#ffc107';
                    
                    if (data.zScore >= 1) {
                      category = 'Excellence (≥+1 SD)';
                      color = '#2e7d32';
                    } else if (data.zScore >= 0) {
                      category = 'Au-dessus (+0 à +1 SD)';
                      color = '#4caf50';
                    } else if (data.zScore >= -1) {
                      category = 'En-dessous (0 à -1 SD)';
                      color = '#ff9800';
                    } else {
                      category = 'Développement (<-1 SD)';
                      color = '#f44336';
                    }
                    
                    return { name, value: data.score, mean: data.mean, zScore: data.zScore.toFixed(2), category, color };
                  });
                  
                  return (
                    <>
                      <div style={{ height: '280px', width: '100%', marginLeft: '-100px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis type="number" domain={[1.0, 4.5]} ticks={[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5]} label={{ value: 'Score', position: 'insideBottom', offset: -5 }} />
                            <YAxis type="category" dataKey="name" width={150} />
                            <Tooltip 
                              formatter={(value, name, props) => [`Score: ${props.payload.value.toFixed(2)} | Z-score: ${props.payload.zScore} | ${props.payload.category}`, 'Analysis']}
                              labelFormatter={(label) => `Attitude: ${label}`}
                            />
                            <Bar dataKey="value">
                              {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', fontSize: '13px', color: '#555' }}>
                        <strong><SafeFormattedMessage msgKey="ATTITUDES_LEGEND_HIGH_PERFORMERS" /></strong>
                        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '8px', flexWrap: 'wrap' }}>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#2e7d32', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_EXCELLENCE" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4caf50', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_ABOVE" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ffc107', marginRight: '15px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_AVERAGE" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#ff9800', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_BELOW" /></span>
                          <span><span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', marginRight: '5px' }}></span><SafeFormattedMessage msgKey="ATTITUDES_SD_DEVELOPMENT" /></span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
{ <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>}
      {/* Page 5: Z-Score Visualization */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_3" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_Z_SCORES" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_STANDARDIZED_POSITION" />
                </p>
                {(() => {
                  const stats = calculateAttitudeStats(scores);
                  if (!stats) return null;
                  
                  const chartData = Object.entries(stats).map(([name, data]) => ({
                    name,
                    value: data.zScore,
                    zScore: data.zScore.toFixed(2),
                    rawScore: data.score.toFixed(2)
                  }));
                  
                  return (
                    <div style={{ height: '260px', width: '100%', marginLeft: '-100px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                          <XAxis type="number" domain={[-2, 2]} ticks={[-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2]} label={{ value: 'Z-Score (Écarts-types)', position: 'insideBottom', offset: -5 }} />
                          <YAxis type="category" dataKey="name" width={150} />
                          <Tooltip 
                            formatter={(value, name, props) => [`Z-score: ${props.payload.zScore} (Raw score: ${props.payload.rawScore})`, 'Normalized Position']}
                            labelFormatter={(label) => `Attitude: ${label}`}
                          />
                          <Bar dataKey="value">
                            {chartData.map((entry, index) => {
                              const z = parseFloat(entry.zScore);
                              let color = '#2196f3';
                              if (z >= 1) color = '#4caf50';
                              else if (z >= 0) color = '#8bc34a';
                              else if (z >= -1) color = '#ff9800';
                              else color = '#f44336';
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
{ <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>}
      {/* Page 6: Reference Group Comparison Table */}
      <div className="PageCharts">
        <div>
          <div className="PageCharts-chart">
            <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'88%' }}>
              <h2 style={{ color: 'var(--ppe-green)', fontSize: '24px', fontWeight: 'bold', marginBottom: '30px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <SafeFormattedMessage msgKey="ATTITUDES_ADDITIONAL_ANALYSIS_4" />
              </h2>
              
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f3e5f5', borderRadius: '8px', border: '1px solid #e1bee7' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_GAP_REFERENCE_GROUP" />
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                  <SafeFormattedMessage msgKey="ATTITUDES_ABSOLUTE_DIFFERENCE_HIGH_PERFORMERS" />
                </p>
                {(() => {
                  const stats = calculateAttitudeStats(scores);
                  if (!stats) return null;
                  
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#9c27b0', color: 'white' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_ATTITUDE" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_CANDIDATE_SCORE" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_REFERENCE_MEAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_REFERENCE_MEDIAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_GAP_MEAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_GAP_MEDIAN" /></th>
                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #7b1fa2' }}><SafeFormattedMessage msgKey="ATTITUDES_INTERPRETATION" /></th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(stats).map(([name, data], idx) => {
                            const diff = data.diffFromMean;
                            let interpretation = '';
                            let bgColor = '#fff';
                            
                            if (diff >= 0.5) {
                              interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_ABOVE" />;
                              bgColor = '#c8e6c9';
                            } else if (diff <= -0.5) {
                              interpretation = <SafeFormattedMessage msgKey="ATTITUDES_CLEARLY_BELOW" />;
                              bgColor = '#ffccbc';
                            } else {
                              interpretation = <SafeFormattedMessage msgKey="ATTITUDES_COMPARABLE" />;
                              bgColor = '#fff9c4';
                            }
                            
                            return (
                              <tr key={name} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                                <td style={{ padding: '12px', fontWeight: '600', borderBottom: '1px solid #e0e0e0' }}><SafeFormattedMessage msgKey={attitudeNameMap[name]} /></td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{data.score.toFixed(2)}</td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{data.mean.toFixed(2)}</td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>{data.median.toFixed(2)}</td>
                                <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', color: diff >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                  {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', color: data.diffFromMedian >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                  {data.diffFromMedian >= 0 ? '+' : ''}{data.diffFromMedian.toFixed(2)}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e0e0e0', backgroundColor: bgColor, fontWeight: '500' }}>
                                  {interpretation}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
              <div style={{ marginBottom: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h3 style={{ color: 'var(--ppe-green)', fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
                 { !embedded  && <span><SafeFormattedMessage msgKey="ATTITUDES_RAW_ATTITUDES_SCORES" /></span> }
                </h3>
                {scores.length > 0 ? (
                  <div style={{ height: '260px', width: '100%', marginLeft: '-100px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {(() => {
                        const chartData = scores.map(att => {
                          const rawScore = typeof att.score === 'number' ? att.score : parseFloat(att.score) || 0;
                          return { name: att.name, value: rawScore, rawScore: rawScore.toFixed(2) };
                        });
                        return (
                          <BarChart layout="vertical" data={chartData} margin={{ top: 20, right: 30, left: 150, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                            <XAxis type="number" domain={[0, 4]} ticks={[0,0.5,1,1.5,2,2.5,3,3.5,4]} label={{ value: '', position: 'insideBottom' }} />
                            <YAxis type="category" dataKey="name" width={150} />
                            <Tooltip formatter={(value, name, props) => [props.payload.rawScore, 'Raw Score']} labelFormatter={(label) => `Attitude: ${label}`} />
                            <Bar dataKey="value">
                              {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={'var(--ppe-green)'} />)}
                            </Bar>
                          </BarChart>
                        );
                      })()}
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}><SafeFormattedMessage msgKey="ATTITUDES_NO_ATTITUDE_DATA" /></p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  ) : null;

  const finalPageJsx = (
    <div className="PageCharts">
      <div>
        <div className="PageCharts-chart">
          <div style={{ height: '845pt', margin: '0 auto', padding: '40px 20px', width:'80%' }}>
            { !embedded  && 
              <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '14px', color: '#6c757d' }}>
                <p style={{ margin: 0 }}><strong><SafeFormattedMessage msgKey="ATTITUDES_SCOPE_ASSESSMENT_SYSTEM" /></strong></p>
                <p style={{ margin: '5px 0 0 0' }}><SafeFormattedMessage msgKey="ATTITUDES_CONFIDENTIAL_REPORT" /></p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );

  // If embedded, render only the inner PageCharts block
  if (embedded) return (
    <>
      {page1Jsx}
      {
      !embedded &&
        <div>
          <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>
          {page2Jsx}
          <div style={{height: '25px',width:'100%',backgroundColor:'white'}}></div>
          {additionalAnalysesJsx}
        </div>
      }
    </>
  );

  // Otherwise render full page with header and navigation
  return (
    <div style={{ backgroundColor: 'white', minHeight: '100vh', color: '#333' }}>
      <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderBottom: '2px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <img src={ppeFullLogo} alt="SCOPE Logo" style={{ height: '40px', width: 'auto' }} />
        <button onClick={onBack} style={{ background: '#6c757d', border: 'none', borderRadius: '6px', padding: '8px 16px', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}><SafeFormattedMessage msgKey="ATTITUDES_BACK_TO_ADMIN" /></button>
      </div>
      <div id="UserInfo">
        <div className="UserInfo-content" style={{ marginTop: '15px' }}>
          {page1Jsx}
          {page2Jsx}
          {additionalAnalysesJsx}
          {!embedded && finalPageJsx}
        </div>
      </div>
    </div>
  );
}
